import type {
  CreateUpstreamModelMappingRequest,
  CreateUpstreamModelMappingResponse,
  DeleteUpstreamModelMappingResponse,
  UpdateUpstreamModelMappingRequest,
  UpdateUpstreamModelMappingResponse,
  UpstreamModelMappingRecord,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { createModelMapping, deleteModelMapping, listModelMappings, updateModelMapping } from './modelMappings.ts';

export function mappingsQueryKey(upstreamId: string): readonly string[] {
  return ['upstream-model-mappings', upstreamId];
}

export function useModelMappingsList(upstreamId: string): UseQueryResult<readonly UpstreamModelMappingRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: mappingsQueryKey(upstreamId),
    queryFn: async () => {
      const res = await listModelMappings(upstreamId, fetchImpl);
      return res.mappings;
    },
  });
}

export function useCreateModelMapping(upstreamId: string): UseMutationResult<CreateUpstreamModelMappingResponse, Error, CreateUpstreamModelMappingRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUpstreamModelMappingRequest) => createModelMapping(upstreamId, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mappingsQueryKey(upstreamId) }); },
  });
}

export function useDeleteModelMapping(upstreamId: string): UseMutationResult<DeleteUpstreamModelMappingResponse, Error, { vendor: string; channel: string; model: string; protocol: string }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params) => deleteModelMapping(upstreamId, params.vendor, params.channel, params.model, params.protocol, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mappingsQueryKey(upstreamId) }); },
  });
}

export function useUpdateModelMapping(upstreamId: string): UseMutationResult<
  UpdateUpstreamModelMappingResponse,
  Error,
  { vendor: string; channel: string; model: string; protocol: string; body: UpdateUpstreamModelMappingRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params) => updateModelMapping(upstreamId, params.vendor, params.channel, params.model, params.protocol, params.body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mappingsQueryKey(upstreamId) }); },
  });
}
